'use client';

// ShareDock (최종지시서_SHARE_20261007) — 공유·복사 버튼 1종. 전 표면 같은 규격: 높이 44 · 라운드 12 · 간격 8 · 폭 1.4 : 1 : 1.
//
//  ① 카카오톡 — Kakao.Share feed(SDK·키가 «있을 때만» 렌더). 말풍선 CI 로고 금지, 텍스트 라벨만.
//  ② 링크 복사 — 순수 URL(UTM 없음, Q-S1). 성공 = 버튼 자체가 「복사됨 ✓」 1.5초(토스트 신설 없음). 실패 = execCommand 폴백 → URL 선택 노출.
//  ③ 공유 — navigator.share(시스템 시트). 미지원이면 «미렌더» — 빈 자리·깨진 버튼 0, 폭은 남은 버튼에 재배분.
//
// ⚠️ SSR 마크업은 ② 하나(항상 동작). ①③ 은 마운트 뒤 조건 충족 시 «붙는다» — 높이는 고정이라 CLS 0.
// ⛔ 흰 바탕에만 둔다(호출부 규칙) · 하단 고정영역·도크 무접촉 · 클로킹 없음(서버·클라 첫 마크업 동일).
// 계측: share_click {method: kakao|copy|native, surface} — UI 와 같은 커밋(lib/rx/track · conversion_events).

import { useCallback, useEffect, useRef, useState } from 'react';
import { trackRx } from '@/lib/rx/track';
import { buildShareUrls, shareDescription, type ShareSurface } from '@/lib/rx/share';
import './share-dock.css';

export interface ShareDockProps {
  /** 페이지의 «깨끗한 원본 주소»(canonical, UTM 없음). 복사값 그대로. */
  url: string;
  title: string;
  /** 정의문·요약 1줄(없으면 기본 문구). 120자 text-safe 절단은 내부에서. */
  description?: string | null;
  /** OG 이미지(없으면 기존 체인 /api/og). */
  imageUrl?: string | null;
  surface: ShareSurface;
  /** /api/share 집계용(기존 공유 카운트·보상 체인 유지). 게시글만. */
  postId?: number | string;
  contentType?: string;
  contentRef?: string;
  className?: string;
}

const KAKAO_KEY = process.env.NEXT_PUBLIC_KAKAO_JS_KEY;

type KakaoLike = { isInitialized: () => boolean; init: (k: string) => void; Share?: { sendDefault: (o: unknown) => void } };

function readyKakao(): KakaoLike | null {
  try {
    const k = (window as unknown as { Kakao?: KakaoLike }).Kakao;
    if (!k || !KAKAO_KEY) return null;
    if (!k.isInitialized()) k.init(KAKAO_KEY);
    return k.isInitialized() && k.Share ? k : null;
  } catch { return null; }
}

export default function ShareDock({ url, title, description, imageUrl, surface, postId, contentType, contentRef, className }: ShareDockProps) {
  const [kakao, setKakao] = useState(false);
  const [native, setNative] = useState(false);
  const [copied, setCopied] = useState(false);
  const [manual, setManual] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const input = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setNative(typeof navigator !== 'undefined' && typeof navigator.share === 'function');
    if (!KAKAO_KEY) return; // 키 부재 = 카카오 버튼 미렌더(Q-S2)
    let n = 0;
    const iv = setInterval(() => {
      n += 1;
      if (readyKakao()) { setKakao(true); clearInterval(iv); } else if (n >= 20) clearInterval(iv); // SDK 는 afterInteractive — 최대 ~6초 대기
    }, 300);
    if (readyKakao()) { setKakao(true); clearInterval(iv); }
    return () => clearInterval(iv);
  }, []);

  useEffect(() => () => { if (timer.current) clearTimeout(timer.current); }, []);

  const count = useCallback((method: string) => {
    trackRx('share_click', method, surface);
    try {
      void fetch('/api/share', {
        method: 'POST', keepalive: true, headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ post_id: typeof postId === 'number' ? postId : undefined, platform: method, content_type: contentType ?? surface, content_ref: contentRef ?? '' }),
      }).catch(() => {});
    } catch { /* 계측은 UI 를 막지 않는다 */ }
  }, [surface, postId, contentType, contentRef]);

  const urls = buildShareUrls(url, surface);

  const onKakao = () => {
    count('kakao');
    const k = readyKakao();
    if (!k) return;
    try {
      const img = imageUrl || `${new URL(url).origin}/api/og?title=${encodeURIComponent(title)}&design=2`;
      k.Share!.sendDefault({
        objectType: 'feed',
        content: { title, description: shareDescription(description), imageUrl: img, link: { mobileWebUrl: urls.kakao, webUrl: urls.kakao } },
        buttons: [{ title: '카더라에서 보기', link: { mobileWebUrl: urls.kakao, webUrl: urls.kakao } }],
      });
    } catch { /* SDK 오류 — 복사 버튼이 남아 있다 */ }
  };

  const onCopy = async () => {
    count('copy');
    let ok = false;
    try { await navigator.clipboard.writeText(urls.copy); ok = true; } catch { /* 폴백 */ }
    if (!ok) {
      try {
        const ta = document.createElement('textarea');
        ta.value = urls.copy; ta.setAttribute('readonly', ''); ta.style.position = 'fixed'; ta.style.opacity = '0';
        document.body.appendChild(ta); ta.select();
        ok = document.execCommand('copy');
        document.body.removeChild(ta);
      } catch { ok = false; }
    }
    if (ok) {
      setManual(false); setCopied(true);
      if (timer.current) clearTimeout(timer.current);
      timer.current = setTimeout(() => setCopied(false), 1500);
    } else {
      setManual(true); // 침묵 금지 — URL 을 선택된 채로 보여 준다
      setTimeout(() => { input.current?.focus(); input.current?.select(); }, 0);
    }
  };

  const onNative = async () => {
    count('native');
    try { await navigator.share({ title, text: shareDescription(description), url: urls.native }); } catch { /* 사용자가 닫음 */ }
  };

  return (
    <div className={['kd-sdock', className].filter(Boolean).join(' ')} data-share-dock={surface}>
      <div className="kd-sdock__row">
        {kakao ? (
          <button type="button" className="kd-sdock__btn kd-sdock__btn--kakao" onClick={onKakao} aria-label="카카오톡으로 공유">카카오톡</button>
        ) : null}
        <button type="button" className={`kd-sdock__btn kd-sdock__btn--copy${copied ? ' is-done' : ''}`} onClick={onCopy} aria-label="링크 복사" aria-live="polite">
          {copied ? '복사됨 ✓' : '링크 복사'}
        </button>
        {native ? (
          <button type="button" className="kd-sdock__btn kd-sdock__btn--more" onClick={onNative} aria-label="다른 앱으로 공유">공유</button>
        ) : null}
      </div>
      {manual ? (
        <input ref={input} className="kd-sdock__manual" readOnly value={urls.copy} aria-label="복사할 주소" onFocus={(e) => e.currentTarget.select()} />
      ) : null}
    </div>
  );
}
